import { defineStore } from "pinia";
import { ref } from "vue";
import service from "@/services/crm/lead.service";
import { toast } from "@/utils/toast";
import { createScopedCache } from "@/stores/_cache";

const leadListCache = createScopedCache("leads:list");
const leadDetailCache = createScopedCache("leads:detail");

const LEAD_LIST_TTL = 2 * 60 * 1000;
const LEAD_DETAIL_TTL = 5 * 60 * 1000;

export const useLeadStore = defineStore("lead", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = ref({
    q: "",
    owner_id: "",
    pipeline_id: "",
    stage_id: "",
    tags: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  function normalize(raw) {
    return {
      id: raw.id,
      name: raw.name,
      party_type: raw.party_type || "Organization",
      pipeline: raw.pipeline || null,
      pipeline_id: raw.pipeline_id ?? raw.pipeline?.id ?? null,
      stage: raw.stage || null,
      stage_id: raw.stage_id ?? raw.stage?.id ?? null,
      owner: raw.owner || null,
      owner_id: raw.owner_id ?? raw.owner?.id ?? null,
      company_name: raw.company_name || "",
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || "KES",
      phone: raw.phone || "",
      phone_label: raw.phone_label || "",
      email: raw.email || "",
      email_label: raw.email_label || "",
      source: raw.source || "",
      industry: raw.industry || "",
      description: raw.description || "",
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      visibility: raw.visibility || "public",
      users: raw.users || [],
      created_at: raw.created_at,
    };
  }

  function replaceList(rows, meta) {
    items.value = rows.map(normalize);
    byId.value = Object.fromEntries(items.value.map((x) => [x.id, x]));
    pagination.value = meta;
  }

  function upsertOne(raw) {
    const n = normalize(raw);
    byId.value[n.id] = n;
    const idx = items.value.findIndex((x) => x.id === n.id);
    if (idx === -1) items.value.unshift(n);
    else items.value.splice(idx, 1, n);
    return n;
  }

  async function fetchAll(extra = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      return await leadListCache.get(params, {
        ttlMs: LEAD_LIST_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await service.list(params);
          const list = data?.data ?? data ?? [];
          return {
            rows: Array.isArray(list) ? list : [],
            meta: {
              total: data?.meta?.total ?? (Array.isArray(list) ? list.length : 0),
              per_page: data?.meta?.per_page ?? params.per_page ?? 25,
              current_page: data?.meta?.current_page ?? params.page ?? 1,
            },
          };
        },
        onHydrate: (payload) => {
          replaceList(payload.rows || [], payload.meta || pagination.value);
        },
      });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch leads";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(extra = {}, options = {}) {
    return fetchAll(extra, options);
  }

  async function fetchOne(id, options = {}) {
    loading.value = true;
    try {
      await leadDetailCache.get(
        { id },
        {
          ttlMs: LEAD_DETAIL_TTL,
          force: options.force === true,
          loader: async () => {
            const { data } = await service.get(id);
            return { row: data };
          },
          onHydrate: (payload) => {
            if (payload?.row) upsertOne(payload.row);
          },
        },
      );
      return byId.value[id] || null;
    } finally {
      loading.value = false;
    }
  }

  async function ensureOne(id, options = {}) {
    return fetchOne(id, options);
  }

  function invalidateLeadCaches() {
    leadListCache.invalidate();
    leadDetailCache.invalidate();
  }

  async function create(payload) {
    loading.value = true;
    try {
      const { data } = await service.create(payload);
      invalidateLeadCaches();
      const created = upsertOne(data);
      toast("Lead created");
      return created;
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to create lead", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      const { data } = await service.update(id, payload);
      leadListCache.invalidate();
      leadDetailCache.invalidate({ id });
      const updated = upsertOne(data);
      toast("Lead updated");
      return updated;
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to update lead", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await service.remove(id);
      invalidateLeadCaches();
      delete byId.value[id];
      const idx = items.value.findIndex((x) => x.id === id);
      if (idx !== -1) items.value.splice(idx, 1);
      toast("Lead deleted");
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to delete lead", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    byId,
    loading,
    error,
    pagination,
    filters,
    fetchAll,
    ensureList,
    fetchOne,
    ensureOne,
    create,
    update,
    remove,
    invalidateLeadCaches,
  };
});
